import { getAuthToken } from '../../Api/authToken';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../../Api/api';
import { FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiUpload, FiDownload } from 'react-icons/fi';
import './AdminPages.css';
import { CONFIG_MODULES, getFieldType } from './WebConfig/WebConfigModules';
import * as XLSX from 'xlsx';

/** Stored image path (filename or uploads/...) → absolute URL for previews */
const resolveUploadSrc = (apiUrl, stored) => {
  if (stored === undefined || stored === null || stored === '') return '';
  const s = String(stored).trim();
  if (/^https?:\/\//i.test(s)) return s;
  const base = apiUrl.replace(/\/api\/?$/, '').replace(/\/+$/, '');
  const p = s.replace(/^\/+/, '');
  const path = p.startsWith('uploads/') ? p : `uploads/${p}`;
  return `${base}/${path}`;
};

// ---------- helpers ----------
const mapIdToName = (key, val, relations, moduleId) => {
  if (val === undefined || val === null) return 'N/A';
  if (key === 'state_id' && relations.states) {
    const s = relations.states.find(x => x.id === Number(val));
    return s ? s.state_name : val;
  }
  if (key === 'city_id' && relations.cities) {
    const c = relations.cities.find(x => x.id === Number(val));
    return c ? c.city_name : val;
  }
  if (key === 'block_key') {
    const list = moduleId === 'projectInput' ? (relations.projectBlocks || []) : (relations.propertyBlocks || []);
    const hit = list.find((b) => String(b.block_key) === String(val));
    return hit ? (hit.block_name || hit.block_key) : val;
  }
  if (key === 'dropdown_id' && relations.dropdowns) {
    const d = relations.dropdowns.find((x) => Number(x.id) === Number(val));
    return d ? d.name : val;
  }
  if (key === 'category_id' && moduleId === 'amenities' && relations.amenityCategories) {
    const c = relations.amenityCategories.find((x) => Number(x.id ?? x.amenities_category_id) === Number(val));
    return c ? c.name : val;
  }
  if (['type_id', 'category_id', 'category_required', 'other_required', 'other_show', 'is_land_primary', 'landtype_ids'].includes(key)) {
    const ids = parseNumberArray(val);
    const source = key === 'type_id'
      ? (moduleId === 'projectInput' ? (relations.projectTypes || []) : (relations.propertyTypes || []))
      : (['is_land_primary', 'landtype_ids'].includes(key) ? (relations.landtypes || []) : (relations.categories || []));
    const nameById = new Map(source.map((item) => [
      Number(item.category_id ?? item.id ?? item.categoryId),
      item.type_name || item.category_name || item.landtype_name || item.name || `#${item.id ?? item.category_id}`
    ]));
    return ids.map((id) => nameById.get(Number(id)) || `#${id}`).join(', ');
  }
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (Array.isArray(val)) return `[${val.length} items]`;
  if (typeof val === 'object') {
    if (val.name !== undefined) return String(val.name);
    if (val.label !== undefined) return String(val.label);
    if (val.value !== undefined) return String(val.value);
    if (val.id !== undefined) return `#${val.id}`;
    return JSON.stringify(val).slice(0, 60);
  }
  return String(val);
};

const authHeaders = () => ({ Authorization: `Bearer ${getAuthToken()}` });
const getCrudMode = (mod) => mod?.crudMode || 'id';
const isSingletonMode = (mod) => getCrudMode(mod) !== 'id';
const canDeleteModule = (mod) => getCrudMode(mod) !== 'singleton-put-no-delete';

// ---------- Module-specific smart form fields ----------
const MODULE_FIELDS = {
  states: [
    { key: 'state_name', label: 'State Name', type: 'text', required: true },
    { key: 'sort_order', label: 'Sort Order', type: 'number' },
    { key: 'is_active', label: 'Is Active', type: 'boolean' },
  ],
  cities: [
    { key: 'city_name', label: 'City Name', type: 'text', required: true },
    { key: 'state_id', label: 'State', type: 'state_select', required: true },
    { key: 'sort_order', label: 'Sort Order', type: 'number' },
    { key: 'is_active', label: 'Is Active', type: 'boolean' },
  ],
  subregisters: [
    { key: 'sub_name', label: 'Sub Register Name', type: 'text', required: true },
    { key: 'city_id', label: 'City', type: 'city_select', required: true },
    { key: 'state_id', label: 'State', type: 'state_select', required: true },
    { key: 'is_active', label: 'Is Active', type: 'boolean' },
  ],
  nearestLocation: [
    { key: 'name', label: 'Location Name', type: 'text', required: true },
    { key: 'distance', label: 'Distance (km)', type: 'number' },
  ],
  categories: [
    { key: 'category_name', label: 'Category Name', type: 'text', required: true },
    { key: 'category_image', label: 'Category Image', type: 'image' },
    { key: 'type_id', label: 'Property Types', type: 'property_type_multi', required: true },
    { key: 'is_active', label: 'Is Active', type: 'boolean' },
    { key: 'is_project', label: 'Is Project Category', type: 'boolean' },
    { key: 'is_other', label: 'Is Other Category', type: 'boolean' }
  ],
  amenities: [
    { key: 'aminitie_name', label: 'Amenity Name', type: 'text', required: true },
    { key: 'category_id', label: 'Amenity Category', type: 'amenity_category_select', required: true },
  ],
  amenities_cat: [
    { key: 'name', label: 'Category Name', type: 'text', required: true },
  ],
  propertyDetail: [
    { key: 'label', label: 'Label', type: 'text', required: true },
    { key: 'field', label: 'Field Key', type: 'text', required: true },
    { key: 'type', label: 'Type', type: 'select', options: ['text','number','area','bool'] },
    { key: 'unit', label: 'Unit', type: 'text' },
    { key: 'detail_show', label: 'Property Detail Categories', type: 'category_multi' },
    { key: 'project_detail_show', label: 'Project Detail Categories', type: 'category_multi' },
  ],
  propertyBlock: [
    { key: 'block_name', label: 'Block Name', type: 'text', required: true },
    { key: 'block_key', label: 'Block Key', type: 'text', required: true },
    { key: 'block_icon', label: 'Block Icon', type: 'text' },
    { key: 'block_type', label: 'Block Type', type: 'text' },
    { key: 'is_property_show', label: 'Is Property Show', type: 'boolean' },
    { key: 'is_project_show', label: 'Is Project Show', type: 'boolean' },
    { key: 'field_keys', label: 'Field Keys', type: 'array' },
    { key: 'enable_toggle', label: 'Enable Toggle', type: 'boolean' },
    { key: 'toggle_field', label: 'Toggle Field', type: 'text' },
    { key: 'default_enabled', label: 'Default Enabled', type: 'boolean' },
  ],
  projectBlock: [
    { key: 'block_name', label: 'Block Name', type: 'text', required: true },
    { key: 'block_key', label: 'Block Key', type: 'text', required: true },
    { key: 'block_icon', label: 'Block Icon', type: 'text' },
    { key: 'block_type', label: 'Block Type', type: 'text' },
    { key: 'is_project_show', label: 'Is Project Show', type: 'boolean' },
    { key: 'is_property_show', label: 'Is Property Show', type: 'boolean' },
    { key: 'field_keys', label: 'Field Keys', type: 'array' },
    { key: 'enable_toggle', label: 'Enable Toggle', type: 'boolean' },
    { key: 'toggle_field', label: 'Toggle Field', type: 'text' },
    { key: 'default_enabled', label: 'Default Enabled', type: 'boolean' },
  ],
  propertyInput: [
    { key: 'field', label: 'Field Key', type: 'text', required: true },
    { key: 'label', label: 'Label', type: 'text', required: true },
    { key: 'type', label: 'Type', type: 'select', required: true, options: ['text', 'number', 'file', 'textarea', 'select', 'checkbox'] },
    { key: 'placeholder', label: 'Placeholder', type: 'text' },
    { key: 'is_show', label: 'Is Show', type: 'boolean' },
    { key: 'block_key', label: 'Block Key', type: 'property_block_select' },
    { key: 'type_id', label: 'Type ID', type: 'property_type_multi' },
    { key: 'category_id', label: 'Category ID', type: 'category_multi' },
    { key: 'category_required', label: 'Category Required', type: 'category_multi' },
    { key: 'other_required', label: 'Other Required', type: 'category_multi' },
    { key: 'other_show', label: 'Other Show', type: 'category_multi' },
    { key: 'is_land_primary', label: 'Is Land Primary', type: 'landtype_multi' },
    { key: 'landtype', label: 'Land Type Enabled', type: 'boolean' },
    { key: 'landtype_ids', label: 'Land Type IDs', type: 'landtype_multi' },
    { key: 'dropdown', label: 'Dropdown', type: 'boolean' },
    { key: 'dropdown_id', label: 'Dropdown Source', type: 'dropdown_select' },
    { key: 'multi_upload', label: 'Multi Upload', type: 'boolean' },
    { key: 'accept', label: 'Accept', type: 'text' },
  ],
  projectInput: [
    { key: 'field', label: 'Field Key', type: 'text', required: true },
    { key: 'label', label: 'Label', type: 'text', required: true },
    { key: 'type', label: 'Type', type: 'select', required: true, options: ['text', 'number', 'file', 'textarea', 'select', 'checkbox'] },
    { key: 'placeholder', label: 'Placeholder', type: 'text' },
    { key: 'is_show', label: 'Is Show', type: 'boolean' },
    { key: 'block_key', label: 'Block Key', type: 'project_block_select' },
    { key: 'type_id', label: 'Type ID', type: 'project_type_multi' },
    { key: 'category_id', label: 'Category ID', type: 'category_multi' },
    { key: 'category_required', label: 'Category Required', type: 'category_multi' },
    { key: 'other_required', label: 'Other Required', type: 'category_multi' },
    { key: 'other_show', label: 'Other Show', type: 'category_multi' },
    { key: 'is_land_primary', label: 'Is Land Primary', type: 'landtype_multi' },
    { key: 'landtype', label: 'Land Type Enabled', type: 'boolean' },
    { key: 'landtype_ids', label: 'Land Type IDs', type: 'landtype_multi' },
    { key: 'dropdown', label: 'Dropdown', type: 'boolean' },
    { key: 'dropdown_id', label: 'Dropdown Source', type: 'dropdown_select' },
    { key: 'multi_upload', label: 'Multi Upload', type: 'boolean' },
    { key: 'accept', label: 'Accept', type: 'text' },
  ],
  bookingForm: [
    { key: 'field', label: 'Field Key', type: 'text', required: true },
    { key: 'label', label: 'Label', type: 'text', required: true },
    { key: 'type', label: 'Type', type: 'select', options: ['text','email','phone','number','textarea','select'] },
    { key: 'required', label: 'Required', type: 'boolean' },
    { key: 'sort_order', label: 'Sort Order', type: 'number' },
  ],
  hero: [
    { key: 'title', label: 'Hero title', type: 'text' },
    { key: 'subtitle', label: 'Hero subtitle', type: 'textarea' },
    { key: 'search_placeholder', label: 'Search placeholder', type: 'text' },
    { key: 'bg_images', label: 'Background images (JSON array of filenames)', type: 'array' },
    { key: 'search_tabs', label: 'Search tabs (JSON array of {id,label})', type: 'array' },
    { key: 'quick_actions', label: 'Quick actions (JSON array)', type: 'array' },
    { key: 'free_banner', label: 'Free banner (JSON object with lines[])', type: 'array' },
  ],
  homeSection: [
    { key: 'id', label: 'Section id (string, optional)', type: 'text' },
    { key: 'flag', label: 'Flag (e.g. is_urgent)', type: 'text', required: true },
    { key: 'header', label: 'Header', type: 'text' },
    { key: 'subheader', label: 'Subheader', type: 'text' },
    { key: 'badge_label', label: 'Badge label', type: 'text' },
    { key: 'badge_color', label: 'Badge color (hex)', type: 'text' },
  ],
  privacy: [
    { key: 'title', label: 'Privacy Policy Title', type: 'text' },
    { key: 'description', label: 'Privacy Policy Description', type: 'textarea', required: true },
    { key: 'sections', label: 'Privacy Sections (JSON Array)', type: 'array' },
  ],
  terms: [
    { key: 'title', label: 'Terms Title', type: 'text' },
    { key: 'description', label: 'Terms Description', type: 'textarea', required: true },
    { key: 'image', label: 'Terms Image', type: 'image' },
    { key: 'sections', label: 'Terms Sections (JSON Array)', type: 'array' },
  ],
  contact: [
    { key: 'title', label: 'Title', type: 'text' },
    { key: 'description', label: 'Description', type: 'textarea' },
    { key: 'heroHeading', label: 'Hero heading', type: 'text' },
    { key: 'heroSub', label: 'Hero subheading', type: 'text' },
    { key: 'heroAvailability', label: 'Hero availability', type: 'text' },
    { key: 'officeLabel', label: 'Office label', type: 'text' },
    { key: 'tagText', label: 'Tag text', type: 'text' },
    { key: 'successMessage', label: 'Success message', type: 'text' },
    { key: 'sendText', label: 'Send button text', type: 'text' },
    { key: 'sentText', label: 'Sent text', type: 'text' },
    { key: 'contactInfo', label: 'Contact info (JSON: phone, email, address)', type: 'array' },
    { key: 'infoItems', label: 'Info items (JSON array)', type: 'array' },
    { key: 'stats', label: 'Stats (JSON array)', type: 'array' },
    { key: 'formFields', label: 'Form fields (JSON array)', type: 'array' },
    { key: 'image', label: 'Image', type: 'image' },
  ],
  socialMedia: [
    { key: 'platform', label: 'Platform', type: 'text', required: true },
    { key: 'url', label: 'URL', type: 'text', required: true },
    { key: 'icon', label: 'Icon', type: 'text' },
    { key: 'label', label: 'Label', type: 'text' },
    { key: 'is_active', label: 'Is Active', type: 'boolean' },
    { key: 'sort_order', label: 'Sort Order', type: 'number' },
  ],
  ads: [
    { key: 'is_active', label: 'Is Active', type: 'boolean' },
    { key: 'property_id', label: 'Property ID', type: 'number' },
    { key: 'title', label: 'Title', type: 'text' },
    { key: 'image', label: 'Ad Image', type: 'image' },
    { key: 'cta_label', label: 'CTA label (optional)', type: 'text' },
    { key: 'cta_url', label: 'CTA URL (optional)', type: 'text' },
    { key: 'duration', label: 'Display delay (ms, web popup)', type: 'number' },
    { key: 'auto_close', label: 'Auto close (ms, web popup)', type: 'number' },
    { key: 'cooldown_hours', label: 'Cooldown hours (web)', type: 'number' },
  ],
  navLink: [
    { key: 'label', label: 'Label', type: 'text', required: true },
    { key: 'url', label: 'URL', type: 'text', required: true },
    { key: 'sort_order', label: 'Sort Order', type: 'number' },
    { key: 'is_active', label: 'Is Active', type: 'boolean' },
  ],
  propertyTypes: [
    { key: 'type_name', label: 'Type Name', type: 'text', required: true },
    { key: 'is_active', label: 'Is Active', type: 'boolean' },
  ],
  projectTypes: [
    { key: 'type_name', label: 'Type Name', type: 'text', required: true },
    { key: 'is_active', label: 'Is Active', type: 'boolean' },
  ],
  listingTypes: [
    { key: 'name', label: 'Listing Type Name', type: 'text', required: true },
    { key: 'is_active', label: 'Is Active', type: 'boolean' },
  ],
  landtype: [
    { key: 'landtype_name', label: 'Land Type Name', type: 'text', required: true },
  ],
  settings: [
    { key: 'site_name', label: 'Site Name', type: 'text' },
    { key: 'logo', label: 'Logo', type: 'image' },
    { key: 'favicon', label: 'Favicon', type: 'image' },
    { key: 'primary_color', label: 'Primary Color', type: 'text' },
    { key: 'meta_title', label: 'Meta Title', type: 'text' },
    { key: 'meta_description', label: 'Meta Description', type: 'textarea' },
  ],
};

const IMPORT_MODULES = {
  states: { headers: ['State'], endpoint: '/states/import' },
  cities: { headers: ['State', 'City'], endpoint: '/cities/import' },
  subregisters: { headers: ['State', 'City', 'Sub Register'], endpoint: '/subregisters/import' }
};

/** Rows per request — keeps payloads under server body limit and avoids long single requests */
const IMPORT_BATCH_SIZE = 1500;

const mergeImportSummaries = (totals, batch) => {
  if (!batch) return totals;
  const next = { ...totals };
  Object.keys(batch).forEach((key) => {
    if (typeof batch[key] === 'number') {
      next[key] = (next[key] || 0) + batch[key];
    }
  });
  return next;
};

/** Rows ready for API import (non-empty required columns per module). */
const getValidImportRows = (moduleId, rows) => {
  if (!Array.isArray(rows)) return [];
  if (moduleId === 'states') {
    return rows.filter((r) => String(r.State ?? '').trim());
  }
  if (moduleId === 'cities') {
    return rows.filter((r) => String(r.State ?? '').trim() && String(r.City ?? '').trim());
  }
  if (moduleId === 'subregisters') {
    return rows.filter(
      (r) =>
        String(r.State ?? '').trim() &&
        String(r.City ?? '').trim() &&
        String(r['Sub Register'] ?? '').trim(),
    );
  }
  return rows;
};

const isImportRowValid = (moduleId, row) =>
  getValidImportRows(moduleId, [row]).length === 1;

const REAL_ESTATE_ARRAY_KEYS = new Set([
  'category_required',
  'type_id',
  'landtype_ids',
  'other_required',
  'other_show',
  'is_land_primary'
]);

const parseNumberArray = (value) => {
  if (Array.isArray(value)) {
    return value
      .flatMap((item) => String(item ?? '').split(','))
      .map((part) => Number(String(part).trim()))
      .filter((n) => !Number.isNaN(n));
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((part) => Number(part.trim()))
      .filter((n) => !Number.isNaN(n));
  }
  if (value === null || value === undefined || value === '') return [];
  const n = Number(value);
  return Number.isNaN(n) ? [] : [n];
};

const parseBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  return Boolean(value);
};

const normalizeSelectValue = (raw) => {
  if (raw === null || raw === undefined) return '';
  if (typeof raw === 'object') {
    if (raw.id !== undefined) return String(raw.id);
    if (raw.value !== undefined) return String(raw.value);
    return '';
  }
  return String(raw);
};

const WebConfig = () => {
  const groupedModules = CONFIG_MODULES.reduce((acc, mod) => {
    if (!acc[mod.group]) acc[mod.group] = [];
    acc[mod.group].push(mod);
    return acc;
  }, {});

  const [activeModule, setActiveModule] = useState(CONFIG_MODULES[0]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [fields, setFields] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [fileObjects, setFileObjects] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showImportModal, setShowImportModal] = useState(false);
  const [importRows, setImportRows] = useState([]);
  const [importHeaders, setImportHeaders] = useState([]);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  // Geography relations for dependent dropdowns
  const [allStates, setAllStates] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [allPropertyBlocks, setAllPropertyBlocks] = useState([]);
  const [allProjectBlocks, setAllProjectBlocks] = useState([]);
  const [allPropertyTypes, setAllPropertyTypes] = useState([]);
  const [allProjectTypes, setAllProjectTypes] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [allLandTypes, setAllLandTypes] = useState([]);
  const [allDropdowns, setAllDropdowns] = useState([]);
  const [allAmenitiesCategories, setAllAmenitiesCategories] = useState([]);

  // Load geography relations — called on mount and after every save
  const refreshRelations = useCallback(() => {
    axios.get(`${API_URL}/states`).then(r => setAllStates(r.data?.data || r.data || [])).catch(() => {});
    axios.get(`${API_URL}/cities`).then(r => setAllCities(r.data?.data || r.data || [])).catch(() => {});
    axios.get(`${API_URL}/property-blocks`).then(r => setAllPropertyBlocks(r.data?.data || r.data || [])).catch(() => {});
    axios.get(`${API_URL}/project-blocks`).then(r => setAllProjectBlocks(r.data?.data || r.data || [])).catch(() => {});
    axios.get(`${API_URL}/types/property`).then(r => setAllPropertyTypes(r.data?.data || r.data || [])).catch(() => {});
    axios.get(`${API_URL}/types/project`).then(r => setAllProjectTypes(r.data?.data || r.data || [])).catch(() => {});
    axios.get(`${API_URL}/category`).then(r => setAllCategories(r.data?.data || r.data || [])).catch(() => {});
    axios.get(`${API_URL}/landtype`).then(r => setAllLandTypes(r.data?.data || r.data || [])).catch(() => {});
    axios.get(`${API_URL}/dropdown`).then(r => setAllDropdowns(r.data?.data || r.data || [])).catch(() => {});
    axios.get(`${API_URL}/amenities-category`).then(r => setAllAmenitiesCategories(r.data?.data || r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    refreshRelations();
  }, [refreshRelations]);

  useEffect(() => {
    fetchData();
    setSelectedIds(new Set());
  }, [activeModule]);

  // Update filtered cities when state changes in form
  useEffect(() => {
    if (formData.state_id) {
      setFilteredCities(allCities.filter(c => c.state_id === Number(formData.state_id)));
    } else {
      setFilteredCities([]);
    }
  }, [formData.state_id, allCities]);

  const getModuleFields = useCallback((mod) => {
    return MODULE_FIELDS[mod.id] || null; // null = fallback to auto-extract
  }, []);

  const extractFields = (items) => {
    if (!items?.length) return [{ key: 'name', label: 'NAME', type: 'text' }];
    const keys = new Set();
    items.forEach(item => Object.keys(item).forEach(k => {
      if (!['_id', '__v', 'createdAt', 'updatedAt'].includes(k) && k !== activeModule.pk) keys.add(k);
    }));
    return Array.from(keys).map(key => {
      let sampleVal = null;
      for (const item of items) {
        if (item[key] !== null && item[key] !== undefined) { sampleVal = item[key]; break; }
      }
      return { key, label: key.replace(/_/g, ' ').toUpperCase(), type: getFieldType(key, sampleVal) };
    });
  };

  const fetchData = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await axios.get(`${API_URL}${activeModule.endpoint}`);
      let items = [];
      if (Array.isArray(res.data)) items = res.data;
      else if (Array.isArray(res.data?.data)) items = res.data.data;
      else if (res.data && typeof res.data === 'object') items = [res.data];
      const normalizedItems = items.map((item) => {
        const next = { ...item };
        if (activeModule.id === 'nearestLocation' && next.near_name && !next.name) {
          next.name = next.near_name;
        }
        if (activeModule.id === 'navLink' && next.path && !next.url) {
          next.url = next.path;
        }
        if (activeModule.id === 'ads') {
          if (next.is_active === undefined || next.is_active === null) next.is_active = true;
        }
        return next;
      });

      setData(normalizedItems);
      const modFields = getModuleFields(activeModule);
      setFields(modFields || extractFields(normalizedItems));
    } catch (error) {
      setLoadError(error?.response?.data?.error || `Failed to load ${activeModule.label}`);
      toast.error(`Failed to load ${activeModule.label}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (item = null) => {
    setEditItem(item);
    setFileObjects({});
    const modFields = getModuleFields(activeModule) || extractFields(data);
    if (item) {
      const cloned = { ...item };
      modFields.forEach((f) => {
        if (['property_block_select', 'project_block_select', 'dropdown_select', 'state_select', 'city_select', 'amenity_category_select'].includes(f.type)) {
          cloned[f.key] = normalizeSelectValue(cloned[f.key]);
        }
        if (['property_type_multi', 'project_type_multi', 'category_multi', 'landtype_multi'].includes(f.type)) {
          cloned[f.key] = parseNumberArray(cloned[f.key]);
        }
      });
      setFormData(cloned);
    } else {
      const init = {};
      modFields.forEach(f => {
        if (activeModule.id === 'ads' && f.key === 'is_active' && f.type === 'boolean') {
          init[f.key] = true;
        } else {
          init[f.key] = f.type === 'boolean' ? false : (f.type === 'number' ? '' : '');
        }
      });
      setFormData(init);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => { setShowModal(false); setEditItem(null); setFormData({}); };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      let payload = { ...formData };
      delete payload.__v;
      delete payload.createdAt;
      delete payload.updatedAt;
      delete payload._id;
      delete payload.id;

      // Convert numbers
      const modFields = getModuleFields(activeModule) || [];
      modFields.forEach(f => {
        if (f.type === 'number') {
          if (payload[f.key] !== '' && payload[f.key] !== null && payload[f.key] !== undefined) {
            payload[f.key] = Number(payload[f.key]);
          } else {
            delete payload[f.key];
          }
        }
      });

      // Normalize known array-of-number fields used in Real Estate modules.
      Object.keys(payload).forEach((key) => {
        if (REAL_ESTATE_ARRAY_KEYS.has(key) && !(activeModule.id === 'amenities' && key === 'category_id')) {
          payload[key] = parseNumberArray(payload[key]);
        } else if (typeof payload[key] === 'string' && (payload[key].trim().startsWith('[') || payload[key].trim().startsWith('{'))) {
          try {
            payload[key] = JSON.parse(payload[key]);
          } catch (e) {
            // Keep as string if JSON parsing fails
          }
        }
      });
      if (Object.prototype.hasOwnProperty.call(payload, 'dropdown_id')) {
        if (typeof payload.dropdown_id === 'object' && payload.dropdown_id !== null) {
          payload.dropdown_id = payload.dropdown_id.id ?? payload.dropdown_id.value ?? '';
        }
        payload.dropdown_id = payload.dropdown_id === '' ? null : Number(payload.dropdown_id);
        if (Number.isNaN(payload.dropdown_id)) payload.dropdown_id = null;
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'dropdown')) {
        payload.dropdown = parseBoolean(payload.dropdown);
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'landtype')) {
        payload.landtype = parseBoolean(payload.landtype);
      }
      if (activeModule.id === 'propertyBlock' || activeModule.id === 'projectBlock') {
        ['is_property_show', 'is_project_show', 'enable_toggle', 'default_enabled'].forEach((key) => {
          if (Object.prototype.hasOwnProperty.call(payload, key)) {
            payload[key] = parseBoolean(payload[key]);
          }
        });
      }
      // Keep category module behavior consistent as well.
      if (activeModule.id === 'categories' && payload.type_id !== undefined) {
        payload.type_id = parseNumberArray(payload.type_id);
      }
      if (activeModule.id === 'nearestLocation' && payload.name !== undefined) {
        payload.near_name = payload.name;
        delete payload.name;
      }
      if (activeModule.id === 'navLink' && payload.url !== undefined) {
        payload.path = payload.url;
        delete payload.url;
      }
      if (activeModule.id === 'ads') {
        const pid = payload.property_id;
        if (pid === undefined || pid === '' || (typeof pid === 'number' && !Number.isFinite(pid))) {
          payload.property_id = null;
        }
      }
      if (activeModule.id === 'amenities' && payload.category_id !== undefined && payload.category_id !== '') {
        const n = Number(payload.category_id);
        payload.category_id = Number.isFinite(n) ? n : null;
      }
      if (activeModule.id === 'hero') {
        ['bg_images', 'search_tabs', 'quick_actions'].forEach((k) => {
          if (typeof payload[k] === 'string' && payload[k].trim()) {
            try {
              payload[k] = JSON.parse(payload[k]);
            } catch (_) {
              /* leave as-is */
            }
          }
          if (payload[k] === '' || payload[k] === undefined || payload[k] === null) {
            payload[k] = [];
          }
        });
        if (typeof payload.free_banner === 'string' && payload.free_banner.trim()) {
          try {
            payload.free_banner = JSON.parse(payload.free_banner);
          } catch (_) {
            payload.free_banner = { lines: [] };
          }
        }
        if (!payload.free_banner || typeof payload.free_banner !== 'object' || Array.isArray(payload.free_banner)) {
          payload.free_banner = { lines: [] };
        }
        if (payload.nearby_radius_km !== undefined && payload.nearby_radius_km !== '') {
          payload.nearby_radius_km = Number(payload.nearby_radius_km);
        }
      }

      // Handle file uploads
      for (const k of Object.keys(fileObjects)) {
        const fm = new FormData();
        fm.append('file', fileObjects[k]);
        try {
          const upRes = await axios.post(`${API_URL}/upload/single`, fm, {
            headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' }
          });
          payload[k] = upRes.data.path?.split('/').pop() || upRes.data.filename;
        } catch {
          toast.error(`Failed to upload ${k}`); return;
        }
      }

      const crudMode = getCrudMode(activeModule);
      if (editItem && crudMode === 'id') {
        const id = editItem[activeModule.pk] || editItem.id || editItem._id;
        await axios.put(`${API_URL}${activeModule.endpoint}/${id}`, payload, { headers: authHeaders() });
        toast.success(`${activeModule.label} updated`);
      } else {
        if (crudMode === 'singleton-put' || crudMode === 'singleton-put-no-delete') {
          await axios.put(`${API_URL}${activeModule.endpoint}`, payload, { headers: authHeaders() });
          toast.success(`${activeModule.label} saved`);
        } else if (crudMode === 'singleton-post') {
          await axios.post(`${API_URL}${activeModule.endpoint}`, payload, { headers: authHeaders() });
          toast.success(`${activeModule.label} saved`);
        } else {
          await axios.post(`${API_URL}${activeModule.endpoint}`, payload, { headers: authHeaders() });
          toast.success(`${activeModule.label} created`);
        }
      }
      handleCloseModal();
      fetchData();
      // Refresh geography dropdowns so new states/cities appear immediately
      refreshRelations();
    } catch (error) {
      toast.error(error.response?.data?.error || error.response?.data?.message || `Failed to save`);
    }
  };

  const handleDelete = async (item) => {
    if (!canDeleteModule(activeModule)) {
      toast.error(`Delete is not supported for ${activeModule.label}`);
      return;
    }
    if (!window.confirm(`Delete this ${activeModule.label}?`)) return;
    try {
      if (isSingletonMode(activeModule)) {
        await axios.delete(`${API_URL}${activeModule.endpoint}`, { headers: authHeaders() });
      } else {
        const id = item[activeModule.pk] || item.id || item._id;
        await axios.delete(`${API_URL}${activeModule.endpoint}/${id}`, { headers: authHeaders() });
      }
      toast.success('Deleted');
      fetchData();
    } catch { toast.error('Delete failed'); }
  };

  const handleBulkDelete = async () => {
    if (isSingletonMode(activeModule)) {
      toast.error(`Bulk delete is not supported for ${activeModule.label}`);
      return;
    }
    if (!window.confirm(`Delete ${selectedIds.size} items?`)) return;
    for (const id of Array.from(selectedIds)) {
      await axios.delete(`${API_URL}${activeModule.endpoint}/${id}`, { headers: authHeaders() }).catch(() => {});
    }
    toast.success(`Deleted ${selectedIds.size} items`);
    setSelectedIds(new Set());
    fetchData();
  };

  const handleInlineToggle = async (item, key, value) => {
    try {
      const id = item[activeModule.pk] || item.id || item._id;
      // Optimistic UI update
      setData(prev => prev.map(d => ((d[activeModule.pk] || d.id || d._id) === id ? { ...d, [key]: value } : d)));
      // Send ONLY the changed field — avoid sending _id/__v/timestamps that may fail validation
      if (isSingletonMode(activeModule)) {
        const mode = getCrudMode(activeModule);
        const singletonPayload = { ...item, [key]: value };
        if (mode === 'singleton-post') {
          await axios.post(`${API_URL}${activeModule.endpoint}`, singletonPayload, { headers: authHeaders() });
        } else {
          await axios.put(`${API_URL}${activeModule.endpoint}`, singletonPayload, { headers: authHeaders() });
        }
      } else {
        await axios.put(`${API_URL}${activeModule.endpoint}/${id}`, { [key]: value }, { headers: authHeaders() });
      }
      toast.success(`${key.replace(/_/g,' ')} updated`);
    } catch { fetchData(); toast.error('Update failed'); }
  };

  const toggleSelect = (id) => {
    const s = new Set(selectedIds);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelectedIds(s);
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === data.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(data.map(d => d[activeModule.pk] || d.id || d._id)));
  };

  const setField = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));

  const getOptionsByType = (type) => {
    if (type === 'property_block_select') return allPropertyBlocks.map(b => ({ value: String(b.block_key || b.id), label: b.block_name || b.block_key || `#${b.id}` }));
    if (type === 'project_block_select') return allProjectBlocks.map(b => ({ value: String(b.block_key || b.id), label: b.block_name || b.block_key || `#${b.id}` }));
    if (type === 'property_type_multi') return allPropertyTypes.map(t => ({ value: String(t.id), label: t.type_name || `#${t.id}` }));
    if (type === 'project_type_multi') return allProjectTypes.map(t => ({ value: String(t.id), label: t.type_name || `#${t.id}` }));
    if (type === 'category_multi') return allCategories.map(c => {
      const id = c.category_id ?? c.id ?? c.categoryId;
      return { value: String(id), label: c.category_name || `#${id}` };
    });
    if (type === 'landtype_multi') return allLandTypes.map(l => ({ value: String(l.id), label: l.landtype_name || `#${l.id}` }));
    if (type === 'dropdown_select') return allDropdowns.map(d => ({ value: String(d.id), label: d.name || `#${d.id}` }));
    if (type === 'amenity_category_select') {
      return allAmenitiesCategories.map((c) => {
        const id = c.id ?? c.amenities_category_id;
        return { value: String(id), label: c.name || `#${id}` };
      });
    }
    return [];
  };

  const renderFormField = (f) => {
    const val = formData[f.key];
    const inputStyle = { width: '100%', padding: '0.625rem 0.75rem', borderRadius: '0.5rem', border: '1px solid var(--admin-border)', fontSize: '0.875rem' };

    if (f.type === 'range') {
      const min = f.min ?? 1;
      const max = f.max ?? 100;
      const step = f.step ?? 1;
      const num = Number(val);
      const safe = Number.isFinite(num) ? Math.min(max, Math.max(min, num)) : min;
      return (
        <div className="admin-range-field">
          <div className="admin-range-row" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <input
              type="range"
              min={min}
              max={max}
              step={step}
              value={safe}
              onChange={(e) => setField(f.key, Number(e.target.value))}
              style={{ flex: 1 }}
            />
            <input
              type="number"
              min={min}
              max={max}
              step={step}
              value={safe}
              onChange={(e) => {
                const next = Number(e.target.value);
                setField(f.key, Number.isFinite(next) ? Math.min(max, Math.max(min, next)) : min);
              }}
              style={{ width: 72, padding: '0.5rem', borderRadius: '0.5rem', border: '1px solid var(--admin-border)' }}
            />
            <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--admin-text-muted)' }}>km</span>
          </div>
          <p style={{ margin: '0.35rem 0 0', fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>
            Nearby property search radius ({min}–{max} km). Changes apply on the next page load or within ~45 seconds.
          </p>
        </div>
      );
    }

    if (f.type === 'boolean') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.25rem' }}>
          <label className="toggle-label" style={{ margin: 0, width: 'fit-content' }}>
            <input type="checkbox" checked={Boolean(val)} onChange={e => setField(f.key, e.target.checked)} />
            <div className="toggle-switch"></div>
          </label>
          <span style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{val ? 'Enabled' : 'Disabled'}</span>
        </div>
      );
    }
    if (f.type === 'textarea') {
      return <textarea rows="4" style={inputStyle} value={val || ''} onChange={e => setField(f.key, e.target.value)} />;
    }
    if (f.type === 'array') {
      let displayValue = val;
      let isComplex = false;
      
      if (Array.isArray(val)) {
        if (val.some(v => typeof v === 'object' && v !== null)) {
          isComplex = true;
          displayValue = JSON.stringify(val, null, 2);
        } else {
          displayValue = val.join(', ');
        }
      } else if (typeof val === 'object' && val !== null) {
        isComplex = true;
        displayValue = JSON.stringify(val, null, 2);
      }

      const isJsonStr = typeof displayValue === 'string' && (displayValue.trim().startsWith('[') || displayValue.trim().startsWith('{'));

      if (isComplex || isJsonStr) {
        return (
          <textarea
            rows="8"
            style={{ ...inputStyle, fontFamily: 'monospace', whiteSpace: 'pre' }}
            value={displayValue || ''}
            onChange={e => setField(f.key, e.target.value)}
            placeholder="Enter JSON array or object"
          />
        );
      }

      return (
        <input
          type="text"
          style={inputStyle}
          value={displayValue || ''}
          onChange={e => setField(f.key, e.target.value)}
          placeholder="Comma separated values"
        />
      );
    }
    if (f.type === 'image') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {(fileObjects[f.key] || val) && (
            <img
              src={fileObjects[f.key] ? URL.createObjectURL(fileObjects[f.key]) : resolveUploadSrc(API_URL, val)}
              alt="preview" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--admin-border)' }}
            />
          )}
          <input type="file" accept="image/*" onChange={e => { if (e.target.files[0]) setFileObjects(p => ({ ...p, [f.key]: e.target.files[0] })); }} />
        </div>
      );
    }
    if (f.type === 'select') {
      return (
        <select style={inputStyle} value={val || ''} onChange={e => setField(f.key, e.target.value)}>
          <option value="">Select…</option>
          {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    if (f.type === 'property_block_select' || f.type === 'project_block_select' || f.type === 'dropdown_select' || f.type === 'amenity_category_select') {
      const opts = getOptionsByType(f.type);
      return (
        <select style={inputStyle} value={val === undefined || val === null ? '' : String(val)} onChange={e => setField(f.key, e.target.value)} required={f.required}>
          <option value="">Select…</option>
          {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }
    if (f.type === 'property_type_multi' || f.type === 'project_type_multi' || f.type === 'category_multi' || f.type === 'landtype_multi') {
      const opts = getOptionsByType(f.type);
      const selectedValues = new Set(parseNumberArray(val).map(String));
      return (
        <select
          multiple
          style={{ ...inputStyle, minHeight: 120 }}
          value={[...selectedValues]}
          onChange={(e) => {
            const next = Array.from(e.target.selectedOptions).map(o => Number(o.value)).filter(n => !Number.isNaN(n));
            setField(f.key, next);
          }}
        >
          {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }
    if (f.type === 'state_select') {
      return (
        <select style={inputStyle} value={val || ''} onChange={e => { setField(f.key, e.target.value); setField('city_id', ''); }} required={f.required}>
          <option value="">Select State</option>
          {allStates.map(s => <option key={s.id} value={s.id}>{s.state_name}</option>)}
        </select>
      );
    }
    if (f.type === 'city_select') {
      return (
        <select style={inputStyle} value={val || ''} onChange={e => setField(f.key, e.target.value)} required={f.required} disabled={!formData.state_id}>
          <option value="">{formData.state_id ? 'Select City' : 'Select State first'}</option>
          {filteredCities.map(c => <option key={c.id} value={c.id}>{c.city_name}</option>)}
        </select>
      );
    }
    return (
      <input
        type={f.type === 'number' ? 'number' : 'text'}
        style={inputStyle}
        value={val === null || val === undefined ? '' : val}
        onChange={e => setField(f.key, e.target.value)}
        required={f.required}
      />
    );
  };

  const activeImportConfig = IMPORT_MODULES[activeModule.id] || null;

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportRows([]);
    setImportHeaders([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleExcelFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file || !activeImportConfig) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const dataBuffer = e.target?.result;
        const workbook = XLSX.read(dataBuffer, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });

        if (!rows.length) {
          toast.error('Excel file is empty');
          return;
        }

        const sheetHeaders = Object.keys(rows[0]).map((h) => String(h).trim());
        const expected = activeImportConfig.headers;
        const hasHeaders = expected.every((h) => sheetHeaders.includes(h));
        if (!hasHeaders) {
          toast.error(`Invalid headers. Expected: ${expected.join(', ')}`);
          return;
        }

        const parsedRows = rows.map((row) => {
          const next = {};
          expected.forEach((h) => {
            next[h] = String(row[h] ?? '').trim();
          });
          return next;
        });
        setImportHeaders(expected);
        setImportRows(parsedRows);
        setShowImportModal(true);
      } catch {
        toast.error('Failed to parse Excel file');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const updateImportCell = (rowIndex, header, value) => {
    setImportRows((prev) =>
      prev.map((row, idx) => (idx === rowIndex ? { ...row, [header]: value } : row))
    );
  };

  const validImportRows = activeModule?.id
    ? getValidImportRows(activeModule.id, importRows)
    : [];
  const skippedImportCount = importRows.length - validImportRows.length;

  const confirmImport = async () => {
    if (!activeImportConfig || !importRows.length) {
      toast.error('No rows available for import');
      return;
    }
    if (!validImportRows.length) {
      toast.error(
        activeModule.id === 'subregisters'
          ? 'No valid rows: every row must have State, City, and Sub Register filled in.'
          : 'No valid rows to import. Check required columns.',
      );
      return;
    }
    if (skippedImportCount > 0) {
      toast.warn(
        `${skippedImportCount.toLocaleString()} row(s) skipped (missing State, City, or Sub Register). Importing ${validImportRows.length.toLocaleString()} valid row(s).`,
        { autoClose: 6000 },
      );
    }
    setImporting(true);
    try {
      const headers = authHeaders();
      const endpoint = `${API_URL}${activeImportConfig.endpoint}`;
      let mergedSummary = null;

      for (let i = 0; i < validImportRows.length; i += IMPORT_BATCH_SIZE) {
        const chunk = validImportRows.slice(i, i + IMPORT_BATCH_SIZE);
        const batchNum = Math.floor(i / IMPORT_BATCH_SIZE) + 1;
        const totalBatches = Math.ceil(validImportRows.length / IMPORT_BATCH_SIZE);
        if (totalBatches > 1) {
          toast.info(`Importing batch ${batchNum} of ${totalBatches}…`, { autoClose: 1500 });
        }
        const res = await axios.post(endpoint, { rows: chunk }, {
          headers,
          timeout: 300000,
        });
        mergedSummary = mergeImportSummaries(mergedSummary, res.data?.summary);
      }

      const summary = mergedSummary;
      toast.success(
        summary
          ? `Import done. Created: ${summary.created || 0}, Updated: ${summary.updated || 0}, Duplicates: ${summary.duplicatesIgnored || 0}${summary.invalid ? `, Invalid: ${summary.invalid}` : ''}`
          : 'Import completed'
      );
      closeImportModal();
      fetchData();
      refreshRelations();
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.error || error.message || 'Import failed';
      toast.error(msg);
    } finally {
      setImporting(false);
    }
  };

  const handleSubregisterExport = async () => {
    try {
      const res = await axios.get(`${API_URL}/subregisters/export`, { headers: authHeaders() });
      const rows = res.data?.rows || [];
      const worksheet = XLSX.utils.json_to_sheet(rows, {
        header: ['State', 'City', 'Sub Register']
      });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Sub Registers');
      XLSX.writeFile(workbook, 'sub-registers.xlsx');
      toast.success('Sub Register Excel downloaded');
    } catch {
      toast.error('Failed to export Excel');
    }
  };

  return (
    <div className="admin-page config-page">
      <div className="page-header">
        <h1>Web Configuration</h1>
        <p>Manage application configuration and content modules.</p>
      </div>

   <div className="config-layout" style={{ height: 'calc(100vh - 180px)', overflow: 'hidden', alignItems: 'stretch' }}>
  {/* Sidebar */}
  <div className="config-sidebar admin-card" style={{ overflowY: 'auto', height: '100%' }}>
          {Object.entries(groupedModules).map(([groupName, modules]) => (
            <div key={groupName} style={{ marginBottom: '1rem' }}>
              <h4 style={{ padding: '0.5rem 1rem', fontSize: '0.6875rem', textTransform: 'uppercase', color: 'var(--admin-text-muted)', letterSpacing: '0.08em' }}>{groupName}</h4>
              <ul className="config-menu">
                {modules.map(mod => (
                  <li key={mod.id} className={activeModule.id === mod.id ? 'active' : ''} onClick={() => setActiveModule(mod)}>
                    {mod.label}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Content */}
      {/* Content */}
  <div className="config-content admin-card" style={{ overflowY: 'auto', height: '100%' }}>
          <div className="flex-header" style={{ marginBottom: '1.5rem' }}>
            <h3>{activeModule.label} Management</h3>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {activeImportConfig && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    style={{ display: 'none' }}
                    onChange={handleExcelFileSelect}
                  />
                  <button
                    className="btn-secondary"
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <FiUpload /> Upload Excel
                  </button>
                </>
              )}
              {activeModule.id === 'subregisters' && (
                <button className="btn-secondary" type="button" onClick={handleSubregisterExport}>
                  <FiDownload /> Download Excel
                </button>
              )}
              {selectedIds.size > 0 && !isSingletonMode(activeModule) && (
                <button className="btn-secondary text-danger" onClick={handleBulkDelete}>
                  <FiTrash2 /> Delete ({selectedIds.size})
                </button>
              )}
              <button className="btn-primary" onClick={() => handleOpenModal()}>
                <FiPlus /> Add New
              </button>
            </div>
          </div>

         <div className="table-wrapper" style={{ overflowX: 'auto' }}>
            {loading ? (
              <div className="skeleton" style={{ height: 300, borderRadius: 8 }}></div>
            ) : loadError ? (
              <div className="empty-state" style={{ padding: '3rem 0' }}>
                <p>{loadError}</p>
                <button className="btn-primary" style={{ marginTop: '1rem' }} onClick={fetchData}>Retry</button>
              </div>
            ) : data.length === 0 ? (
              <div className="empty-state" style={{ padding: '3rem 0' }}>
                <p>No records found for {activeModule.label}</p>
                <button className="btn-primary" style={{ marginTop: '1rem' }} onClick={() => handleOpenModal()}>Create First Record</button>
              </div>
            ) : (
              <table className="admin-hybrid-table" style={{ width: '100%', fontSize: '0.875rem' }}>
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>
                      <input type="checkbox" checked={selectedIds.size === data.length && data.length > 0} onChange={toggleSelectAll} />
                    </th>
                    <th>ID</th>
                    {fields.slice(0, 4).map(f => <th key={f.key}>{f.label || f.key}</th>)}
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, idx) => {
                    const id = item[activeModule.pk] || item.id || item._id;
                    return (
                      <tr key={id || idx}>
                        <td><input type="checkbox" checked={selectedIds.has(id)} onChange={() => toggleSelect(id)} /></td>
                        <td><span className="hybrid-id" title={String(id)}>#{String(id).slice(-6)}</span></td>
                        {fields.slice(0, 4).map(f => (
                          <td key={f.key}>
                            {f.type === 'boolean' ? (
                              <label className="toggle-label" style={{ width: 'fit-content' }}>
                                <input type="checkbox" checked={Boolean(item[f.key])} onChange={e => handleInlineToggle(item, f.key, e.target.checked)} />
                                <div className="toggle-switch"></div>
                              </label>
                            ) : f.type === 'array' ? (
                              <span className="info-pill">{Array.isArray(item[f.key]) ? `${item[f.key].length} items` : 'Array'}</span>
                            ) : f.type === 'image' || f.type === 'image' ? (
                              item[f.key] ? <img src={resolveUploadSrc(API_URL, item[f.key])} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} /> : 'No image'
                            ) : (
                                <span style={{ maxWidth: 200, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {mapIdToName(f.key, item[f.key], {
                                    states: allStates,
                                    cities: allCities,
                                    propertyBlocks: allPropertyBlocks,
                                    projectBlocks: allProjectBlocks,
                                    propertyTypes: allPropertyTypes,
                                    projectTypes: allProjectTypes,
                                    categories: allCategories,
                                    landtypes: allLandTypes,
                                    dropdowns: allDropdowns,
                                    amenityCategories: allAmenitiesCategories
                                  }, activeModule.id)}
                                </span>
                              )}
                          </td>
                        ))}
                        <td>
                          <div className="table-actions" style={{ justifyContent: 'flex-end' }}>
                            <button className="btn-icon" onClick={() => handleOpenModal(item)}><FiEdit2 /></button>
                            {canDeleteModule(activeModule) && (
                              <button className="btn-icon text-danger" onClick={() => handleDelete(item)}><FiTrash2 /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* CRUD Modal */}
      {showModal && (
        <div className="admin-modal-overlay" style={{ zIndex: 9999 }} onClick={handleCloseModal}>
          <div className="admin-modal" style={{ maxWidth: 600, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editItem ? 'Edit' : 'Create'} {activeModule.label}</h2>
              <button className="btn-icon" onClick={handleCloseModal}><FiX /></button>
            </div>
            <div style={{ overflowY: 'auto', padding: '1.5rem' }}>
              <form onSubmit={handleSave}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
                  {fields.map(f => (
                    <div
                      key={f.key}
                      className="form-group"
                      style={{ gridColumn: (f.type === 'textarea' || f.type === 'image') ? '1 / -1' : 'auto' }}
                    >
                      <label style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.375rem', display: 'block' }}>
                        {f.label || f.key.replace(/_/g, ' ')}
                        {f.required && <span style={{ color: 'var(--admin-danger, #ef4444)', marginLeft: 4 }}>*</span>}
                      </label>
                      {renderFormField(f)}
                    </div>
                  ))}
                </div>

                {/* Submit buttons — NOT full width */}
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '2rem', justifyContent: 'flex-end' }}>
                  <button type="button" className="btn-secondary" onClick={handleCloseModal}>Cancel</button>
                  <button type="submit" className="btn-primary">
                    <FiSave /> {editItem ? 'Save Changes' : 'Create Record'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showImportModal && (
        <div className="admin-modal-overlay" style={{ zIndex: 10000 }} onClick={closeImportModal}>
          <div
            className="admin-modal"
            style={{ maxWidth: 900, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Preview & Edit Import Rows</h2>
              <button className="btn-icon" onClick={closeImportModal}><FiX /></button>
            </div>
            <div style={{ padding: '1rem 1.5rem', overflow: 'auto' }}>
              <p style={{ marginBottom: '0.75rem', color: 'var(--admin-text-muted)' }}>
                Review and edit data before final import.
                {activeModule?.id === 'subregisters' && (
                  <span style={{ display: 'block', marginTop: 6 }}>
                    Each row needs <strong>State</strong>, <strong>City</strong>, and <strong>Sub Register</strong> filled in.
                  </span>
                )}
              </p>
              {importRows.length > 0 && (
                <p style={{ marginBottom: '0.75rem', fontSize: '0.875rem' }}>
                  <span style={{ color: '#15803d', fontWeight: 600 }}>
                    Valid: {validImportRows.length.toLocaleString()}
                  </span>
                  {skippedImportCount > 0 && (
                    <span style={{ color: '#b45309', fontWeight: 600, marginLeft: 12 }}>
                      Skipped (incomplete): {skippedImportCount.toLocaleString()}
                    </span>
                  )}
                </p>
              )}
              <table className="admin-hybrid-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>#</th>
                    {importHeaders.map((header) => <th key={header}>{header}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {importRows.map((row, idx) => {
                    const rowValid = activeModule?.id ? isImportRowValid(activeModule.id, row) : true;
                    return (
                    <tr key={`import-${idx}`} style={!rowValid ? { backgroundColor: '#fff7ed' } : undefined}>
                      <td>{idx + 1}</td>
                      {importHeaders.map((header) => (
                        <td key={`${header}-${idx}`}>
                          <input
                            type="text"
                            value={row[header] || ''}
                            onChange={(e) => updateImportCell(idx, header, e.target.value)}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: `1px solid ${!rowValid && header === 'Sub Register' ? '#f59e0b' : 'var(--admin-border)'}`,
                              borderRadius: 6,
                            }}
                          />
                        </td>
                      ))}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1rem' }}>
                <button className="btn-secondary" type="button" onClick={closeImportModal}>Cancel</button>
                <button
                  className="btn-primary"
                  type="button"
                  onClick={confirmImport}
                  disabled={importing || validImportRows.length === 0}
                >
                  <FiSave /> {importing ? 'Importing...' : `Import ${validImportRows.length.toLocaleString()} row(s)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebConfig;
